import { authenticationStore } from "@/state/auth/AuthStore";
import {
  requestPostGenerateEditorUserAllOfficesToken,
  requestPostGenerateEditorUserToken,
  requestPostGetCallbackUrl,
  requestPostLinkOfficeToToken,
  requestPostLinkOfficeUserToToken,
} from "@/state/editorToken/EditorTokenEffects";
import { AuthenticationContext } from "@/type/core/auth/auth.types";
import { toastError, toastSuccess } from "@/utils/helpers/toast-helper";
import SubmitButton from "@components/core/buttons/SubmitButton";
import { SelectFormField } from "@components/core/inputs/SelectFormField";
import PageLayout from "@components/core/layouts/PageLayout";
import CardSection from "@components/core/section/CardSection";
import { ROUTES } from "@routes/Routes";
import { EDITOR_TOKEN_TYPE, formValidateTriggers } from "@utils/Constant";
import { Col, Form, Row, Spin } from "antd";
import { useUnit } from "effector-react";
import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

const SECONDS_BEFORE_REDIRECTION = 5;

const EditorTokenCallbackScreen: FunctionComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const idRequest = params.idRequest;
  const shortcut = params.shortcut;
  const token = params.token;
  const type = params.type;

  const authStore = useUnit<AuthenticationContext | null>(authenticationStore);

  const [callbackURL, setCallbackURL] = useState<string>();
  const [tokenGenerated, setTokenGenerated] = useState<boolean>(false);
  const offices = authStore?.user?.offices;
  const isExpertComptableOrCollaborateur =
    offices &&
    offices.some((office) => office.expertComptable || office.collaborateur);

  const handleTimerComplete = useCallback(() => {
    if (tokenGenerated && callbackURL) {
      location.href = callbackURL;
    }
  }, [tokenGenerated, callbackURL]);

  const [count, setCount] = useState<number>();

  useEffect(() => {
    if (count === undefined) {
      return () => null;
    }
    // If we’ve reached 0, call onComplete (if any) and stop.
    if (count <= 0) {
      handleTimerComplete();
      return () => null;
    } else {
      const timerId = window.setTimeout(() => {
        setCount(count - 1);
      }, 1000);

      return () => {
        clearTimeout(timerId);
      };
    }
  }, [count, handleTimerComplete]);

  useEffect(() => {
    if (tokenGenerated && callbackURL) {
      setCount(SECONDS_BEFORE_REDIRECTION);
    }
  }, [tokenGenerated, callbackURL]);

  useEffect(() => {
    if (!type || !shortcut || !idRequest) {
      return;
    } else if (type === EDITOR_TOKEN_TYPE.USER && token) {
      void requestPostGenerateEditorUserToken({
        dto: {
          shortcut,
          idRequest,
          token,
        },
      });
    } else if (type === EDITOR_TOKEN_TYPE.ALL_EDITOR_USER) {
      void requestPostGenerateEditorUserAllOfficesToken({
        dto: {
          shortcut,
          idRequest,
        },
      });
    }
  }, [type, shortcut, idRequest, token]);

  useEffect(() => {
    if (idRequest) {
      void requestPostGetCallbackUrl({ dto: { idRequest } });
    }
  }, [idRequest]);

  useEffect(() => {
    return requestPostGenerateEditorUserToken.doneData.watch((response) => {
      if (response.ok) {
        toastSuccess(t("success.editorToken.userCreation"));
        setTokenGenerated(true);
      } else {
        toastError(t("error.toast.editorToken.creationUser"));
      }
    });
  });

  useEffect(() => {
    return requestPostGenerateEditorUserAllOfficesToken.doneData.watch(
      (response) => {
        if (response.ok) {
          toastSuccess(t("success.editorToken.officesAndUserCreation"));
          setTokenGenerated(true);
        } else {
          toastError(t("error.toast.editorToken.officesAndUserCreation"));
        }
      },
    );
  });

  useEffect(() => {
    return requestPostGetCallbackUrl.doneData.watch((response) => {
      if (response.ok && response.data) {
        setCallbackURL(response.data);
      }
    });
  });

  const handleSubmit = (value: { office: string }) => {
    const officeId = value.office;
    if (!type || !shortcut || !idRequest || !officeId) {
      console.error(type, shortcut, idRequest, officeId);
      return;
    } else if (type === EDITOR_TOKEN_TYPE.EDITOR_USER) {
      requestPostLinkOfficeUserToToken({
        dto: {
          shortcut,
          idRequest,
          officeId,
        },
      })
        .then((response) => {
          if (response.ok) {
            toastSuccess(t("success.editorToken.linkUser"));
            setTokenGenerated(true);
          } else {
            toastError(t("error.toast.editorToken.linkUser"));
          }
        })
        .catch(() => {
          toastError(t("error.toast.editorToken.linkUser"));
        });
    } else if (type === EDITOR_TOKEN_TYPE.EDITOR) {
      requestPostLinkOfficeToToken({
        dto: {
          shortcut,
          idRequest,
          officeId,
        },
      })
        .then((response) => {
          if (response.ok) {
            toastSuccess(t("success.editorToken.linkUserAndOffice"));
            setTokenGenerated(true);
          } else {
            toastError(t("error.toast.editorToken.linkUserAndOffice"));
          }
        })
        .catch(() => {
          toastError(t("error.toast.editorToken.linkUserAndOffice"));
        });
    }
  };

  const tokenGeneratedDiv = (accessGenerated: string) => {
    return (
      <div className="white-space-pre-line d-flex flex-column gap-8">
        <p>{accessGenerated}</p>
        {callbackURL && (
          <p>{t("editor.callbackUrl", { callbackURL, seconds: count })}</p>
        )}
      </div>
    );
  };

  if (
    type === EDITOR_TOKEN_TYPE.EDITOR ||
    type === EDITOR_TOKEN_TYPE.EDITOR_USER
  ) {
    return (
      <PageLayout container bodyClassName="mt-0">
        <CardSection title={t("editor.selectOfficeTitle")}>
          <>
            {isExpertComptableOrCollaborateur && !tokenGenerated && (
              <Row className="justify-content-center">
                <Col span={24} lg={20} xl={16}>
                  <Form
                    onFinish={handleSubmit}
                    {...formValidateTriggers}
                    layout="vertical"
                  >
                    <SelectFormField
                      initialValue={offices && offices[0].id}
                      showLabel
                      labelClassName="text-dark-grey"
                      module="signature.accordion.officeForm"
                      field="office"
                      options={offices.map((office) => {
                        return {
                          value: office.id,
                          label: office.alias,
                        };
                      })}
                    />
                    <div className="d-flex align-items-center justify-content-end pt-30">
                      <SubmitButton
                        label={"buttons.validate"}
                        className="mw-160"
                      />
                    </div>
                  </Form>
                </Col>
              </Row>
            )}
            {tokenGenerated && tokenGeneratedDiv(t(`editor.${type}Generated`))}
          </>
        </CardSection>
      </PageLayout>
    );
  } else if (
    type === EDITOR_TOKEN_TYPE.USER ||
    type === EDITOR_TOKEN_TYPE.ALL_EDITOR_USER
  ) {
    return (
      <PageLayout container bodyClassName="mt-0">
        <CardSection title={t("editor.userTokenGeneratedToken")}>
          <>
            {tokenGenerated && tokenGeneratedDiv(t("editor.userGenerated"))}
            {!tokenGenerated && (
              <div className="h-100 flex-grow-1 d-flex align-items-center justify-content-center my-48">
                <Spin />
              </div>
            )}
          </>
        </CardSection>
      </PageLayout>
    );
  } else {
    navigate(ROUTES.extranet.home.generate());
    return null;
  }
};

export default EditorTokenCallbackScreen;
